// Centralised content for Vichu's portfolio.

export const profile = {
    handle: "vichu",
    name: "Vichu",
    fullName: "Viswanatha Kartha V",
    role: "Software Engineer · Tinkerer · Builder",
    location: "Kerala, India",
    tagline:
        "Software engineer, infra tinkerer, and builder of strange useful things.",
    heroIntro:
        "I’m a software engineer, student, and tinkerer building tools across backend systems, cloud infrastructure, AI, developer tooling, and product experiments. I like taking messy ideas and turning them into working systems.",
    links: {
        github: "https://github.com/anima-regem",
        linkedin: "https://www.linkedin.com/in/viswanathakarthav/",
        email: "mailto:hello@karthav.in",
        emailRaw: "hello@karthav.in",
        resume: "#",
    },
};

export const navLinks = [
    { label: "About", href: "#about" },
    { label: "Projects", href: "#projects" },
    { label: "Stack", href: "#stack" },
    { label: "Stories", href: "#stories" },
    { label: "Community", href: "#community" },
    { label: "Contact", href: "#contact" },
];

export const buildAreas = [
    {
        id: "backend",
        title: "Backend & Systems",
        icon: "Server",
        description:
            "APIs, services, data pipelines, automation tools, and backend systems built with practical reliability in mind.",
        tags: ["Go", "Redis", "REST APIs", "SQL", "Linux", "Docker"],
    },
    {
        id: "cloud",
        title: "Cloud & Infrastructure",
        icon: "Cloud",
        description:
            "Hands-on with Kubernetes, cloud deployments, observability, cluster operations, and service debugging.",
        tags: [
            "Kubernetes",
            "Prometheus",
            "Grafana",
            "Loki",
            "OpenTelemetry",
            "IBM Cloud",
        ],
    },
    {
        id: "ai",
        title: "AI Tools",
        icon: "Sparkles",
        description:
            "Experiments with LLM-powered docs, planning, incident response, audio processing, and assistant-style tools.",
        tags: ["LLMs", "LangGraph", "Gemini", "Vertex AI", "SageMaker"],
    },
    {
        id: "apps",
        title: "Apps & Products",
        icon: "AppWindow",
        description:
            "Product-focused apps with real user flows, clean specs, local-first thinking, and practical UX.",
        tags: ["Flutter", "Supabase", "Next.js", "React", "SvelteKit"],
    },
    {
        id: "stories",
        title: "Storytelling & Content",
        icon: "BookOpen",
        description:
            "Narrative experiments around strange technology history, obscure inventions, and the human stories behind systems.",
        tags: ["Tech history", "Documentary", "Short-form video"],
    },
];

export const projects = [
    {
        id: "ai-doc-assistant",
        title: "AI Developer Documentation Assistant",
        type: "Developer Tool / SaaS",
        description:
            "Documentation assistant for software teams that generates API docs, wikis, and multi-page technical documentation from codebases.",
        stack: [
            "VS Code Extension",
            "GitHub App",
            "LLM Backend",
            "SaaS",
        ],
        status: "In progress",
        link: "#",
    },
    {
        id: "incident-response",
        title: "Intelligent Incident Response Agent",
        type: "AI · DevOps · Observability",
        description:
            "AI-powered incident response that analyses simulated logs and metrics, detects issues like memory leaks or API timeouts, performs RCA, and suggests safe remediations.",
        stack: ["Kubernetes", "Prometheus", "LangGraph", "LLMs", "Docker"],
        status: "Hackathon / Prototype",
        link: "#",
    },
    {
        id: "vending-rl",
        title: "Vending Machine Price Optimization",
        type: "AI · Reinforcement Learning",
        description:
            "Reinforcement learning system optimising vending machine prices based on sales patterns, seasonality, and profit targets.",
        stack: ["Python", "RL", "SageMaker", "Dashboards"],
        status: "Research / Prototype",
        link: "#",
    },
    {
        id: "glitch",
        title: "Glitch",
        type: "Productivity App",
        description:
            "Minimal local-first day tracker with task focus, habits, streaks, project milestones, backup/restore, and a swipe-based interface.",
        stack: ["Flutter", "Local Storage", "Mobile UX"],
        status: "Concept / PRD",
        link: "#",
    },
    {
        id: "oasis-lite",
        title: "Oasis Lite",
        type: "Project Management App",
        description:
            "Project tracking and finance management for teams — orgs, projects, epics, tasks, RBAC, payment requests, approvals, and dashboards.",
        stack: ["Flutter", "Supabase", "RBAC", "Dashboards"],
        status: "Concept / PRD",
        link: "#",
    },
    {
        id: "gallery-declutter",
        title: "Gallery Declutterer",
        type: "Mobile Utility",
        description:
            "Helps users clean their gallery via a full-screen swipe interface for keeping, deleting, tagging, and organising images and videos.",
        stack: ["Flutter", "Media Access", "Local Workflows"],
        status: "Concept",
        link: "#",
    },
    {
        id: "vyajan",
        title: "Vyajan",
        type: "Content Tool",
        description:
            "Social media content generation and publishing workflow supporting Twitter, LinkedIn, and Instagram.",
        stack: ["Automation", "Social APIs", "Content Generation"],
        status: "Active / Evolving",
        link: "#",
    },
];

export const stack = [
    {
        group: "Languages",
        items: ["Go", "Python", "JavaScript", "TypeScript", "Java", "R", "SQL"],
    },
    {
        group: "Frontend",
        items: ["React", "Next.js", "Svelte / SvelteKit", "Flutter", "Tailwind"],
    },
    {
        group: "Backend & Infra",
        items: [
            "Go APIs",
            "Redis",
            "Docker",
            "Kubernetes",
            "Linux",
            "REST APIs",
            "Cloud",
        ],
    },
    {
        group: "Observability & DevOps",
        items: [
            "Prometheus",
            "Grafana",
            "Loki",
            "OpenTelemetry",
            "kubectl",
            "CI/CD",
        ],
    },
    {
        group: "AI / ML",
        items: [
            "LLMs",
            "LangChain",
            "LangGraph",
            "Reinforcement Learning",
            "Audio ML",
            "Gemini",
            "Vertex AI",
            "SageMaker",
        ],
    },
    {
        group: "Tools & Platforms",
        items: [
            "GitHub",
            "VS Code Extensions",
            "Supabase",
            "IBM Cloud",
            "Google Cloud",
            "Final Cut Pro",
            "HandBrake",
        ],
    },
];

export const stories = [
    {
        id: "trojan-room",
        title: "The Trojan Room Coffee Pot",
        blurb:
            "How researchers trying to avoid empty coffee accidentally helped invent one of the earliest webcams.",
        meta: "1991 · Cambridge",
    },
    {
        id: "cmu-coke",
        title: "CMU Coke Machine",
        blurb:
            "Remote status checks, lazy engineers, and one of the earliest internet-connected appliances.",
        meta: "1982 · Carnegie Mellon",
    },
    {
        id: "cosmic-glitch",
        title: "Cosmic Glitch",
        blurb:
            "Cosmic rays, memory errors, and the terrifying fragility of digital certainty.",
        meta: "Ongoing · Everywhere",
    },
    {
        id: "wifi-blackhole",
        title: "Wi-Fi’s Black Hole Origin",
        blurb:
            "How research into faint radio signals from black holes helped shape wireless communication.",
        meta: "1990s · CSIRO",
    },
];

export const community = [
    {
        title: "FOSS Club",
        description:
            "Active member and contributor — helping make open source feel approachable.",
    },
    {
        title: "Hacktoberfest Drives",
        description:
            "Organised contribution drives lowering the fear around first PRs.",
    },
    {
        title: "Technical Quizzes",
        description:
            "Designed and ran tech quizzes for students across colleges.",
    },
    {
        title: "First-Year Dev Sessions",
        description:
            "Sessions teaching freshers Git, Linux, and how to debug instead of panic.",
    },
    {
        title: "Hackathon Mentoring",
        description:
            "Helped plan, judge, and mentor hackathon teams from idea to demo.",
    },
    {
        title: "GitHub Campus Experts",
        description:
            "Completed the community assessment phase of the program.",
    },
];

export const workStyle = [
    {
        n: "01",
        text: "I start with a clear problem before choosing the stack.",
    },
    {
        n: "02",
        text: "I prefer small working systems over large imaginary architectures.",
    },
    {
        n: "03",
        text: "I care about developer experience, documentation, and debugging paths.",
    },
    {
        n: "04",
        text: "Infrastructure work fascinates me — it reveals how software behaves when things stop being ideal.",
    },
    {
        n: "05",
        text: "I like products that feel simple on the surface but solve messy problems underneath.",
    },
    {
        n: "06",
        text: "I write PRDs and specs to make vague ideas buildable.",
    },
];
